import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Icon from '../../components/common/Icon';
import { FileUploader, SimpleUploadCard } from './Floor.style';
import FloorList from './Floor';
import { getBuildingAction, getFloorAction, createBuildingAction, createFloorAction, uploadFloorImageAction, } from '../../store/floorSlice';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import { _changeBuildingName } from '../../shared/httpservice/apis';
import { PageContent, PageContentHeader, PageContentWrapper, } from '../../components/layouts/Wrapper.style';
import OnboardingBackground from '../../components/layouts/OnboardingBackground';
import Loader from '../../components/common/Loader';
const BasemapUpload = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [buildingInfo, setBuildingInfo] = useState([]); // Combined data for buildings and floors
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const siteId = useSelector((state) => state?.site?.checkSite?.response?.data?.siteId);
    // Fetch buildings and floors on component mount
    useEffect(() => {
        if (siteId) {
            dispatch(getBuildingAction(siteId))
                .then(async (response) => {
                const buildingsData = response?.payload?.data?.content || [];
                // If buildings exist, fetch floors for each building
                if (buildingsData?.length > 0) {
                    const combinedDataPromises = buildingsData?.map(async (building) => {
                        const floorsResponse = await dispatch(getFloorAction({
                            siteId,
                            data: { buildingId: building?.buildingId, pageSize: 50 },
                        }));
                        const floorsData = floorsResponse?.payload?.data?.content || [];
                        // Combine building and its floors into a single object
                        return {
                            ...building,
                            floors: floorsData,
                        };
                    });
                    // Wait for all promises to resolve and set the combined data to buildingInfo
                    const combinedData = await Promise.all(combinedDataPromises);
                    setBuildingInfo(combinedData);
                }
                setLoading(false);
            })
                .catch(() => {
                setLoading(false);
                message.error('Failed to fetch buildings.');
            });
        }
    }, [dispatch, siteId]);
    // Convert file to Base64 and remove prefix
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file); // Read file as a Base64 string
            reader.onload = () => {
                const imageString = reader.result;
                const base64Data = imageString.replace(/^data:image\/[a-z]+;base64,/, ''); // Remove the base64 prefix
                resolve(base64Data); // Return the cleaned base64 string
            };
            reader.onerror = (error) => reject(error);
        });
    };
    // Extract the dimensions of the image (width and height)
    const getImageDimensions = (base64String) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = `data:image/jpeg;base64,${base64String}`; // Set the Base64 string as the image source
            img.onload = () => resolve({
                imageWidth: img.naturalWidth,
                imageHeight: img.naturalHeight,
            });
            img.onerror = (error) => reject(error);
        });
    };
    const handleFileSelected = (file) => {
        setUploadedFiles((prevFiles) => [...prevFiles, file]);
    };
    const handleUpload = async () => {
        if (uploadedFiles?.length === 0 || !siteId)
            return;
        setLoading(true);
        try {
            // 1. Create building
            const buildingResponse = await dispatch(createBuildingAction({ name: 'Building 1', siteId }));
            const buildingId = buildingResponse?.payload?.data?.buildingId;
            // 2. Create floor(s) and upload image(s)
            for (const [index, file] of uploadedFiles?.reverse()?.entries()) {
                const base64String = await convertFileToBase64(file);
                // Get the dimensions of the image
                const { imageWidth, imageHeight } = await getImageDimensions(base64String);
                // Create a floor for each file
                const floorResponse = await dispatch(createFloorAction({
                    name: file.name,
                    shortName: `Floor ${index}`,
                    elevation: index,
                    width: imageWidth,
                    height: imageHeight,
                    siteId,
                    buildingId,
                }));
                const floorId = floorResponse?.payload?.data?.floorId;
                if (index === 0) {
                    await _changeBuildingName(buildingId, siteId, 'Building 1', floorId, false);
                }
                // Upload the floor image
                await dispatch(uploadFloorImageAction({
                    siteId,
                    floorId,
                    data: { image: base64String },
                }));
            }
            // 3. Refetch buildings and floors
            await fetchBuildingsAndFloors(); // Call function to refetch buildings and floors
            message.success('Buildings, floors created, and floor images uploaded successfully!');
        }
        catch (error) {
            message.error('Upload failed.');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (uploadedFiles?.length) {
            setTimeout(() => {
                handleUpload();
            }, 10);
        }
    }, [uploadedFiles]);
    useEffect(() => {
        if (buildingInfo?.length === 0) {
            setUploadedFiles([]);
        }
    }, [buildingInfo]);
    // Helper function to refetch buildings and floors and update the combined data
    const fetchBuildingsAndFloors = async () => {
        if (siteId) {
            const buildingsResponse = await dispatch(getBuildingAction(siteId));
            const buildingsData = buildingsResponse?.payload?.data?.content || [];
            const combinedDataPromises = buildingsData.map(async (building) => {
                const floorsResponse = await dispatch(getFloorAction({ siteId, data: { buildingId: building?.buildingId, pageSize: 50 } }));
                const floorsData = floorsResponse?.payload?.data?.content || [];
                return {
                    ...building,
                    floors: floorsData,
                };
            });
            const combinedData = await Promise.all(combinedDataPromises);
            setBuildingInfo(combinedData);
        }
    };
    if (loading) {
        return _jsx(Loader, {});
    }
    return (_jsxs(DndProvider, { backend: HTML5Backend, children: [buildingInfo?.length === 0 && _jsx(OnboardingBackground, {}), _jsx(PageContent, { center: buildingInfo?.length === 0 ? true : false, children: _jsx(PageContentWrapper, { width: buildingInfo?.length === 0 ? 960 : '', children: buildingInfo.length === 0 ? (_jsxs(_Fragment, { children: [_jsxs(PageContentHeader, { children: [_jsx("h1", { children: "Upload Your First Map" }), _jsx("p", { children: "To get started with your first navigation, simply upload the floorplans, update the relevant data, and your navigation will be ready to go!" })] }), _jsx(SimpleUploadCard, { children: _jsxs(FileUploader, { accept: ".png,.jpg,.jpeg", beforeUpload: (file) => {
                                        const isAllowedType = ['image/png', 'image/jpeg'].includes(file.type);
                                        if (!isAllowedType) {
                                            message.error(`${file.name} is not a supported file type.`);
                                        }
                                        return isAllowedType;
                                    }, multiple: true, className: "base-upload-ins", showUploadList: false, customRequest: (e) => handleFileSelected(e.file), children: [_jsx("span", { children: _jsx(Icon, { icon: "upload-cloud-01", size: 40 }) }), _jsx("h5", { children: "Click or drag in to upload floor plans" }), _jsx("p", { children: "Only *.png and *.jpg image files are accepted" })] }) })] })) : (_jsx(FloorList, { fetchBuildingsAndFloors: fetchBuildingsAndFloors, buildingInfo: buildingInfo })) }) })] }));
};
export default BasemapUpload;
